import { useEffect } from 'react';
import { isBlocked, isMod, isTextField } from '../ui/keys';

/**
 * The board's own two:
 *
 *   Cmd/Ctrl + N            add an item to the container you're looking at
 *   Cmd/Ctrl + Backspace    delete the selection, with confirmation
 *   Esc                     drop the selection
 *
 * Escape is only taken while something is selected. With nothing selected it
 * belongs to the item panel, which uses it to close — and two handlers racing
 * for one key is how a keystroke starts meaning different things on different
 * days.
 */
export function useBoardKeys({
  enabled,
  hasSelection,
  onAdd,
  onDeleteSelection,
  onClearSelection,
}: {
  /** False while a dialog owns the keyboard. */
  enabled: boolean;
  hasSelection: boolean;
  onAdd: () => void;
  onDeleteSelection: () => void;
  onClearSelection: () => void;
}): void {
  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || isBlocked()) return;
      const typing = isTextField(event.target);

      if (isMod(event) && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        onAdd();
        return;
      }

      // Ctrl+Backspace deletes the previous word inside a field. Taking that
      // over to delete a dozen items is not a trade anyone would accept.
      if (event.key === 'Backspace' && isMod(event)) {
        if (typing || !hasSelection) return;
        event.preventDefault();
        onDeleteSelection();
        return;
      }

      if (event.key === 'Escape' && hasSelection && !typing) {
        event.preventDefault();
        onClearSelection();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, hasSelection, onAdd, onDeleteSelection, onClearSelection]);
}
